import React, { useState, useEffect, useRef } from 'react';
import { SurveyData, User, UserRole, DynamicQuestion, SurveySection } from '../types';
import { Plus, Trash2, Save, User as UserIcon, ArrowRight, ArrowLeft, Download, Loader, Camera, Image as ImageIcon, X } from 'lucide-react';
import { saveSurvey, getTemplate, getDynamicQuestions, getNextSerialNo, getDynamicSections } from '../services/storage';
import { drawCardOnCanvas, downloadCanvas } from '../utils/cardUtils';
import { useToast } from './Toast';

interface SurveyFormProps {
  user: User;
  onSuccess: () => void;
  initialData?: SurveyData | null;
}

// Image Compression Helper - Optimized for Firestore (Max 1MB total doc size)
// We need to support multiple photos (e.g., 3 photos + data).
// Target: ~200KB per image.
const compressImageForDb = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Drastically reduce resolution for safety
        let width = img.width;
        let height = img.height;
        const MAX_DIM = 800; // Reduced from 1200 to 800
        
        if (width > height) {
            if (width > MAX_DIM) {
                height *= MAX_DIM / width;
                width = MAX_DIM;
            }
        } else {
            if (height > MAX_DIM) {
                width *= MAX_DIM / height;
                height = MAX_DIM;
            }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        if(!ctx) { reject(new Error("Canvas failed")); return; }
        
        ctx.drawImage(img, 0, 0, width, height);

        // Iterative compression to hit target size (~200KB)
        // 200KB ~= 270,000 base64 characters
        let quality = 0.7;
        let dataUrl = canvas.toDataURL('image/jpeg', quality);
        
        while (dataUrl.length > 270000 && quality > 0.2) {
            quality -= 0.1;
            dataUrl = canvas.toDataURL('image/jpeg', quality);
        }

        resolve(dataUrl);
      };
      img.onerror = (e) => reject(e);
    };
    reader.onerror = (e) => reject(e);
  });
};

const QuestionCard: React.FC<{ label: string, required?: boolean, children: React.ReactNode }> = ({ label, required, children }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm mb-4 hover:shadow-md transition-shadow border-l-4 border-l-transparent hover:border-l-blue-500 w-full block animate-in fade-in zoom-in-95 duration-200">
    <label className="block text-base font-bold text-gray-800 mb-3 w-full">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="w-full block">{children}</div>
  </div>
);

const ImageInput = ({ value, onChange, onProcessingChange }: { value: string | undefined, onChange: (val: string) => void, onProcessingChange: (status: boolean) => void }) => {
  const [compressing, setCompressing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setCompressing(true);
      onProcessingChange(true);
      try {
        const compressedBase64 = await compressImageForDb(file);
        onChange(compressedBase64);
      } catch (err) {
        alert("Error processing image. Please try again.");
      } finally {
        setCompressing(false);
        onProcessingChange(false);
      }
    }
  };

  const clearImage = () => {
    onChange('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="w-full">
       <input 
         type="file" 
         accept="image/*" 
         capture="environment"
         className="hidden" 
         ref={fileInputRef}
         onChange={handleFileChange}
       />
       
       {!value && (
         <button 
            type="button" 
            onClick={() => fileInputRef.current?.click()}
            disabled={compressing}
            className="w-full h-40 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-500 hover:bg-gray-50 hover:border-emerald-400 hover:text-emerald-600 transition-colors"
         >
            {compressing ? <Loader className="animate-spin mb-2" /> : <Camera size={40} className="mb-2" />}
            <span className="font-bold text-base">
              {compressing ? 'Compressing...' : 'Take Photo / Upload'}
            </span>
            <span className="text-xs text-gray-400 mt-1">Optimized for database storage</span>
         </button>
       )}

       {value && (
         <div className="relative w-full bg-gray-100 rounded-lg overflow-hidden border border-gray-200 flex flex-col items-center justify-center p-2">
            <img src={value} alt="Uploaded" className="max-h-[400px] w-auto rounded shadow-sm" />
            
            <div className="w-full flex justify-between items-center mt-2 px-2">
               <span className="text-xs text-green-600 font-bold flex items-center"><Save size={12} className="mr-1"/> Image Saved</span>
               <button 
                  type="button" 
                  onClick={clearImage}
                  className="bg-red-100 text-red-600 px-3 py-1 rounded text-sm hover:bg-red-200 font-bold"
               >
                  Remove
               </button>
            </div>
         </div>
       )}
    </div>
  );
};

const YesNoInput = ({ value, onChange }: { value: boolean | string | undefined, onChange: (val: any) => void }) => {
  const isYes = value === true || value === 'Yes' || value === 'true';
  const isNo = value === false || value === 'No' || value === 'false';
  return (
    <div className="flex gap-4 w-full">
      <button type="button" onClick={() => onChange(true)} className={`flex-1 py-3 px-4 rounded-lg border-2 flex items-center justify-center space-x-2 transition-all ${isYes ? 'border-emerald-500 bg-emerald-600 text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-emerald-300'}`}>
        <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${isYes ? 'border-white bg-white text-emerald-600' : 'border-gray-300'}`}>{isYes && <div className="w-2.5 h-2.5 rounded-full bg-emerald-600" />}</div><span className="font-bold">Yes / ഉണ്ട്</span>
      </button>
      <button type="button" onClick={() => onChange(false)} className={`flex-1 py-3 px-4 rounded-lg border-2 flex items-center justify-center space-x-2 transition-all ${isNo ? 'border-red-500 bg-red-600 text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-red-300'}`}>
        <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${isNo ? 'border-white bg-white text-red-600' : 'border-gray-300'}`}>{isNo && <div className="w-2.5 h-2.5 rounded-full bg-red-600" />}</div><span className="font-bold">No / ഇല്ല</span>
      </button>
    </div>
  );
};

const MultiSelect = ({ options, value, onChange }: { options: string[], value: string[], onChange: (v: string[]) => void }) => {
  const selected = value || [];
  const toggle = (opt: string) => {
    if (selected.includes(opt)) onChange(selected.filter(s => s !== opt));
    else onChange([...selected, opt]);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {options.map(opt => (
        <label key={opt} className={`flex items-center space-x-3 p-3 rounded cursor-pointer border transition-all ${selected.includes(opt) ? 'bg-emerald-50 border-emerald-500' : 'bg-white border-gray-200 hover:border-emerald-300'}`}>
           <input type="checkbox" className="w-5 h-5 accent-emerald-600" checked={selected.includes(opt)} onChange={() => toggle(opt)} />
           <span className="text-gray-800 font-medium">{opt}</span>
        </label>
      ))}
    </div>
  )
}

const InputClass = "w-full border border-gray-300 p-3 rounded bg-white text-gray-900 focus:ring-2 focus:ring-emerald-500 outline-none block";

const SurveyForm: React.FC<SurveyFormProps> = ({ user, onSuccess, initialData }) => {
  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [questions, setQuestions] = useState<DynamicQuestion[]>([]);
  const [sections, setSections] = useState<SurveySection[]>([]);
  const { addToast } = useToast();
  const [isMediaProcessing, setIsMediaProcessing] = useState(false);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  
  // Data State: Now we store everything in a generic record
  const [formData, setFormData] = useState<Record<string, any>>({});
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [cardTemplate, setCardTemplate] = useState<any>(null);

  useEffect(() => {
    const init = async () => {
      try {
        const [qs, secs, tmpl] = await Promise.all([getDynamicQuestions(), getDynamicSections(), getTemplate()]);
        setQuestions(qs);
        setSections(secs);
        setCardTemplate(tmpl);

        if (initialData) {
          // Flatten existing known sections into the main formData map for ease of use
          // We will restructure on save
          // Fix: Explicitly type spread to avoid potential "Spread types may only be created from object types" error
          const loadedData = { ...(initialData as Record<string, any>) };
          setFormData(loadedData);
        } else {
          const nextSerial = await getNextSerialNo();
          setFormData({
            sectionA: { serialNo: nextSerial },
            // Initialize Repeater sections as arrays
            // Fix: Explicitly type the reduce accumulator to avoid spread type error (commonly at line 241/249)
            ...secs.filter(s => s.isRepeater).reduce<Record<string, any>>((acc, s) => ({ ...acc, [s.id]: [{ id: Date.now().toString() }] }), {})
          });
        }
      } catch (e) {
        console.error("Init failed", e);
        addToast("Failed to initialize form", "error");
      } finally {
        setInitLoading(false);
      }
    };
    init();
  }, [initialData]);

  // Card Redraw
  useEffect(() => {
    // Check if we are at the preview step (which is sections.length)
    if (step === sections.length && canvasRef.current && cardTemplate) {
      // Aggregate data from all sections so photo (in section C) and details (in Section A) are both available
      // Fix: Explicitly type the reduce accumulator for data aggregation to avoid spread type error
      const allData = Object.values(formData).reduce<Record<string, any>>((acc, curr) => {
         if (typeof curr === 'object' && curr !== null && !Array.isArray(curr)) {
             return { ...acc, ...(curr as Record<string, any>) };
         }
         return acc;
      }, {});
      
      drawCardOnCanvas(canvasRef.current, cardTemplate, allData);
    }
  }, [step, formData, cardTemplate, sections]);

  // Logic to check visibility
  const isVisible = (q: DynamicQuestion, data: Record<string, any>) => {
    if (!q.dependsOn) return true;
    const parentVal = data[q.dependsOn];
    if (q.visibleIf === undefined) return !!parentVal; // Just needs a value
    
    // Handle Boolean checks (Yes/No)
    if (typeof q.visibleIf === 'boolean') {
      return parentVal === q.visibleIf || parentVal === String(q.visibleIf);
    }
    // Handle Array checks
    if (Array.isArray(q.visibleIf)) {
      return q.visibleIf.includes(parentVal);
    }
    // Equality
    return parentVal === q.visibleIf;
  };

  const renderQuestions = (sectionId: string, data: Record<string, any>, updater: (k: string, v: any) => void) => {
    return questions
      .filter(q => q.section === sectionId)
      .filter(q => isVisible(q, data))
      .map(q => (
        <QuestionCard key={q.id} label={q.label} required={q.required}>
          {q.id === 'serialNo' ? (
             <input 
               className={`${InputClass} ${user.role !== UserRole.MASTER_ADMIN ? 'bg-gray-100 text-gray-500' : 'bg-white'}`} 
               value={data[q.id] || ''} 
               onChange={e => user.role === UserRole.MASTER_ADMIN && updater(q.id, e.target.value)}
               readOnly={user.role !== UserRole.MASTER_ADMIN} 
             />
          ) : q.type === 'yesno' ? (
            <YesNoInput value={data[q.id]} onChange={v => updater(q.id, v)} />
          ) : q.type === 'image' ? (
            <ImageInput 
                value={data[q.id]} 
                onChange={v => updater(q.id, v)} 
                onProcessingChange={setIsMediaProcessing}
            />
          ) : q.type === 'date' ? (
            <input 
              type="date"
              className={InputClass}
              value={data[q.id] || ''} 
              onChange={e => updater(q.id, e.target.value)}
            />
          ) : q.type === 'dropdown' ? (
            <select className={InputClass} value={data[q.id] || ''} onChange={e => updater(q.id, e.target.value)}>
              <option value="">Select Option</option>
              {q.options?.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : q.type === 'multiselect' ? (
             <MultiSelect options={q.options || []} value={data[q.id]} onChange={v => updater(q.id, v)} />
          ) : q.type === 'textarea' ? (
             <textarea className={InputClass} rows={3} value={data[q.id] || ''} onChange={e => updater(q.id, e.target.value)} />
          ) : (
             <input 
               type={q.id === 'phone' ? 'text' : q.type} 
               className={InputClass} 
               value={data[q.id] || ''} 
               onChange={e => {
                   // Ensure phone numbers are treated as text even if configured as number
                   if (q.id === 'phone') {
                       updater(q.id, e.target.value);
                   } else {
                       updater(q.id, q.type === 'number' && e.target.value !== '' ? parseFloat(e.target.value) : e.target.value);
                   }
               }} 
             />
          )}
        </QuestionCard>
      ));
  };

  const handleDownloadCard = () => {
    if (canvasRef.current) {
       try {
           const filename = `id_card_${formData['sectionA']?.serialNo || 'draft'}.png`;
           downloadCanvas(canvasRef.current, filename);
           setHasDownloaded(true);
       } catch (e) {
           console.error(e);
           if (confirm("Download failed (possibly due to browser settings). Do you want to mark it as downloaded anyway so you can submit?")) {
               setHasDownloaded(true);
           }
       }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isMediaProcessing) return; // Prevent submission while processing
    
    // Only check download requirement if template exists
    if (cardTemplate && !hasDownloaded && isLastStep) {
        addToast("Please download the ID card before submitting.", "error");
        return;
    }
    
    setLoading(true);
    const surveyData: SurveyData = {
      id: initialData ? initialData.id : Date.now().toString(),
      submittedBy: user.id,
      submittedAt: new Date().toISOString(),
      ...formData
    };
    
    try {
      await saveSurvey(surveyData);
      addToast('Survey saved successfully!', 'success');
      onSuccess();
    } catch (e: any) {
      console.error(e);
      // Give more specific feedback if possible
      if (e.message && e.message.includes('exceeds the maximum size')) {
         addToast('Failed: Images are too large. Please remove some photos or try again.', 'error');
      } else {
         addToast('Failed to save survey. Please check connection.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (isMediaProcessing) {
      addToast("Please wait for image to finish processing", "info");
      return;
    }
    setStep(s => s + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const prevStep = () => {
    setStep(s => s - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Generic Update Handler
  // Fix: Explicitly cast to Record<string, any> to avoid spread error from 'any' types
  const updateSectionData = (sectionId: string, key: string, val: any) => {
    const rawData = formData[sectionId];
    // Ensure currentSectionData is an object to prevent spread error on primitives
    const currentSectionData = (rawData && typeof rawData === 'object' && !Array.isArray(rawData)) ? (rawData as Record<string, any>) : {};
    
    setFormData({
      ...formData,
      [sectionId]: { ...currentSectionData, [key]: val }
    });
  };

  // Repeater Handlers (e.g., Members)
  // Fix: Explicitly cast to Record<string, any> to avoid spread error from 'any' types
  const updateRepeaterItem = (sectionId: string, idx: number, key: string, val: any) => {
    const list = formData[sectionId];
    // Ensure we are working with an array
    const items = Array.isArray(list) ? [...list] : [];
    
    // Ensure the specific item exists and is an object
    const rawItem = items[idx];
    const currentItem = (rawItem && typeof rawItem === 'object') ? (rawItem as Record<string, any>) : {};
    
    items[idx] = { ...currentItem, [key]: val };
    setFormData({ ...formData, [sectionId]: items });
  };
  
  const addRepeaterItem = (sectionId: string) => {
    const items = [...(formData[sectionId] || [])];
    items.push({ id: Date.now().toString() });
    setFormData({ ...formData, [sectionId]: items });
  };

  const removeRepeaterItem = (sectionId: string, idx: number) => {
    const items = [...(formData[sectionId] || [])];
    setFormData({ ...formData, [sectionId]: items.filter((_, i) => i !== idx) });
  };

  if (initLoading) {
     return <div className="flex h-64 items-center justify-center"><Loader className="animate-spin text-emerald-600" size={32} /></div>
  }

  // Current Section
  const currentSection = sections[step];
  const isLastStep = step === sections.length; // Preview Step

  // Determine if this is the last data entry step (before preview)
  const isLastDataStep = step === sections.length - 1;

  return (
    <div className="max-w-4xl mx-auto w-full">
      {/* Steps UI */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6 sticky top-0 z-20 border-b border-gray-100 w-full overflow-x-auto">
         <div className="flex justify-between items-center min-w-[300px]">
            <h2 className="text-xl font-bold text-emerald-800 truncate mr-2">
               {isLastStep ? 'Preview & ID Card' : currentSection?.label}
            </h2>
            <div className="flex space-x-1 flex-shrink-0">
               {/* Show indicators for all sections + preview */}
               {[...sections, {id: 'preview'}].map((_, i) => (
                 <div key={i} className={`h-2 w-4 md:w-6 rounded-full transition-all ${i === step ? 'bg-emerald-600' : 'bg-gray-200'}`}/>
               ))}
            </div>
         </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 pb-20 w-full">
         
         {!isLastStep && currentSection && (
            <>
              {currentSection.isRepeater ? (
                // Repeater Rendering (Array)
                <div className="space-y-8 w-full">
                    {(formData[currentSection.id] || []).map((item: any, idx: number) => (
                      <div key={item.id || idx} className="border-2 border-emerald-100 rounded-xl p-6 bg-white relative w-full">
                        <div className="flex justify-between mb-4 border-b pb-2">
                            <h4 className="font-bold text-emerald-800 flex items-center"><UserIcon className="mr-2"/> Entry {idx+1}</h4>
                            <button type="button" onClick={() => removeRepeaterItem(currentSection.id, idx)} className="text-red-500 hover:bg-red-50 p-1 rounded"><Trash2/></button>
                        </div>
                        {renderQuestions(currentSection.id, item, (k, v) => updateRepeaterItem(currentSection.id, idx, k, v))}
                      </div>
                    ))}
                    <button type="button" onClick={() => addRepeaterItem(currentSection.id)} className="w-full py-4 border-2 border-dashed border-emerald-300 text-emerald-700 rounded-lg font-bold flex justify-center items-center hover:bg-emerald-50">
                      <Plus className="mr-2"/> Add Entry
                    </button>
                </div>
              ) : (
                // Standard Single Object Rendering
                renderQuestions(currentSection.id, formData[currentSection.id] || {}, (k, v) => updateSectionData(currentSection.id, k, v))
              )}
            </>
         )}

         {isLastStep && (
            <div className="flex flex-col items-center w-full space-y-6">
                {cardTemplate ? (
                  <div className="space-y-4 w-full max-w-md">
                    <canvas ref={canvasRef} className="w-full h-auto shadow-lg rounded bg-white border" />
                    <button type="button" onClick={handleDownloadCard} className="w-full bg-blue-600 text-white font-bold py-3 rounded-lg flex justify-center items-center"><Download className="mr-2"/> Download ID Card</button>
                  </div>
                ) : <div className="p-4 bg-yellow-50 text-yellow-800 rounded flex flex-col items-center text-center">
                    <p className="font-bold mb-2">No ID Card Template Configured</p>
                    <p className="text-sm">Please contact the Master Admin to upload a template in the 'ID Cards' section.</p>
                    <p className="text-xs text-gray-500 mt-2">You can still submit the survey without generating a card.</p>
                </div>}
            </div>
         )}

         {/* Nav Buttons */}
         <div className="fixed bottom-0 left-0 md:left-64 right-0 bg-white p-4 border-t flex justify-between z-20 shadow-lg">
            {step > 0 ? (
               <button type="button" onClick={prevStep} className="px-6 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 border"><ArrowLeft className="inline mr-1"/> Back</button>
            ) : <div/>}
            
            {!isLastStep ? (
               <div className="flex space-x-2">
                  <button 
                    type="button" 
                    onClick={nextStep} 
                    disabled={isMediaProcessing}
                    className={`px-8 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 shadow font-bold flex items-center ${isMediaProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isMediaProcessing ? 'Processing...' : (isLastDataStep ? 'Preview' : 'Next')} 
                    {!isMediaProcessing && <ArrowRight className="inline ml-1"/>}
                  </button>
               </div>
            ) : (
               <button 
                type="submit" 
                disabled={loading || isMediaProcessing || (!!cardTemplate && !hasDownloaded)} 
                className={`px-8 py-2 text-white rounded shadow font-bold flex items-center transition-all ${!!cardTemplate && !hasDownloaded ? 'bg-gray-400 cursor-not-allowed' : 'bg-emerald-800 hover:bg-emerald-900'}`}
               >
                 {loading ? <Loader className="animate-spin mr-2" size={16}/> : <Save className="inline mr-1"/>} 
                 {!!cardTemplate && !hasDownloaded ? 'Download Card First' : 'Submit Survey'}
               </button>
            )}
         </div>
      </form>
    </div>
  );
};

export default SurveyForm;